import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import "./AssistantContent.scss";
import label from "../../../../i18n/label";
import Message from "../../../../i18n/Message";
import CheckIcon from "../../../../assets/icons/CheckIcon";
import Spinner from "../../../../assets/icons/Spinner";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import TrashIcon from "../../../../assets/icons/TrashIcon";
import { BookIcon, UnplugIcon } from "lucide-react";
import { updateAssistant } from "../store/assistantActionCreators";
import { loadKBases } from "../../knowledge/store/kbaseActionCreators";
import { kbaseSelectors } from "../../knowledge/store/kbaseReducer";
import { loadToolPlugins } from "../../tools/store/toolPluginActionCreators";
import { toolPluginSelectors } from "../../tools/store/toolPluginReducer";

const AssistantContent = ({ assistant, workspaceId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [description, setDescription] = useState(assistant.description || "");
  const [systemPrompt, setSystemPrompt] = useState(
    assistant.systemPrompt || ""
  );
  const [userPrompt, setUserPrompt] = useState(assistant.userPrompt || "");
  const [welcomeMessage, setWelcomeMessage] = useState(
    assistant.welcomeMessage || ""
  );
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Agentic mode state
  const [agenticMode, setAgenticMode] = useState(assistant.agenticMode || false);
  const [maxAgenticRounds, setMaxAgenticRounds] = useState(assistant.maxAgenticRounds || 10);
  const [agenticTimeoutMinutes, setAgenticTimeoutMinutes] = useState(assistant.agenticTimeoutMinutes || 30);

  // Plugin state
  const [selectedPluginUid, setSelectedPluginUid] = useState("");
  const [pluginUids, setPluginUids] = useState(assistant.toolPluginUids || []);
  const availablePlugins = useSelector(toolPluginSelectors.getList);

  // Knowledge base state
  const [selectedKBaseUid, setSelectedKBaseUid] = useState("");
  const [kbaseUids, setKbaseUids] = useState(assistant.knowledgeBaseUids || []);
  const availableKBases = useSelector(kbaseSelectors.getList);

  // Load plugins and knowledge bases
  useEffect(() => {
    dispatch(loadToolPlugins(intl, workspaceId));
    dispatch(loadKBases(intl, workspaceId));
  }, [dispatch, intl, workspaceId]);

  useEffect(() => {
    if (assistant) {
      setDescription(assistant.description || "");
      setSystemPrompt(assistant.systemPrompt || "");
      setUserPrompt(assistant.userPrompt || "");
      setWelcomeMessage(assistant.welcomeMessage || "");
      setPluginUids(assistant.toolPluginUids || []);
      setKbaseUids(assistant.knowledgeBaseUids || []);
      setAgenticMode(assistant.agenticMode || false);
      setMaxAgenticRounds(assistant.maxAgenticRounds || 10);
      setAgenticTimeoutMinutes(assistant.agenticTimeoutMinutes || 30);
      setHasChanges(false);
    }
  }, [assistant?.uid]);

  useEffect(() => {
    const descriptionChanged = description !== (assistant.description || "");
    const systemPromptChanged = systemPrompt !== (assistant.systemPrompt || "");
    const userPromptChanged = userPrompt !== (assistant.userPrompt || "");
    const welcomeMessageChanged = welcomeMessage !== (assistant.welcomeMessage || "");
    const pluginsChanged =
      JSON.stringify(pluginUids.sort()) !==
      JSON.stringify((assistant.toolPluginUids || []).sort());
    const kbasesChanged =
      JSON.stringify(kbaseUids.sort()) !==
      JSON.stringify((assistant.knowledgeBaseUids || []).sort());
    const agenticModeChanged = agenticMode !== (assistant.agenticMode || false);
    const maxAgenticRoundsChanged = maxAgenticRounds !== (assistant.maxAgenticRounds || 10);
    const agenticTimeoutMinutesChanged = agenticTimeoutMinutes !== (assistant.agenticTimeoutMinutes || 30);

    setHasChanges(
      descriptionChanged ||
      systemPromptChanged ||
      userPromptChanged ||
      welcomeMessageChanged ||
      pluginsChanged ||
      kbasesChanged ||
      agenticModeChanged ||
      maxAgenticRoundsChanged ||
      agenticTimeoutMinutesChanged
    );
  }, [
    description,
    systemPrompt,
    userPrompt,
    welcomeMessage,
    pluginUids,
    kbaseUids,
    agenticMode,
    maxAgenticRounds,
    agenticTimeoutMinutes,
    assistant?.description,
    assistant?.systemPrompt,
    assistant?.userPrompt,
    assistant?.welcomeMessage,
    assistant?.toolPluginUids,
    assistant?.knowledgeBaseUids,
    assistant?.agenticMode,
    assistant?.maxAgenticRounds,
    assistant?.agenticTimeoutMinutes,
  ]);

  const onDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
  };

  const onSystemPromptChange = (e) => {
    const value = e.target.value;
    setSystemPrompt(value);
  };

  const onUserPromptChange = (e) => {
    const value = e.target.value;
    setUserPrompt(value);
  };

  const onWelcomeMessageChange = (e) => {
    const value = e.target.value;
    setWelcomeMessage(value);
  };

  const onAddPlugin = () => {
    if (selectedPluginUid && !pluginUids.includes(selectedPluginUid)) {
      setPluginUids([...pluginUids, selectedPluginUid]);
      setSelectedPluginUid("");
    }
  };

  const onRemovePlugin = (uid) => {
    setPluginUids(pluginUids.filter((id) => id !== uid));
  };

  const onAddKBase = () => {
    if (selectedKBaseUid && !kbaseUids.includes(selectedKBaseUid)) {
      setKbaseUids([...kbaseUids, selectedKBaseUid]);
      setSelectedKBaseUid("");
    }
  };

  const onRemoveKBase = (uid) => {
    setKbaseUids(kbaseUids.filter((id) => id !== uid));
  };

  const getPluginById = (uid) => availablePlugins?.find((p) => p.uid === uid);
  const getKBaseById = (uid) => availableKBases?.find((kb) => kb.uid === uid);

  // Filter out already added items
  const availablePluginsFiltered =
    availablePlugins?.filter((p) => !pluginUids.includes(p.uid)) || [];
  const availableKBasesFiltered =
    availableKBases?.filter((kb) => !kbaseUids.includes(kb.uid)) || [];

  const onSave = () => {
    setSaveInProgress(true);
    const onFinished = () => {
      setSaveInProgress(false);
      setHasChanges(false);
    };
    dispatch(
      updateAssistant(
        intl,
        workspaceId,
        assistant.uid,
        {
          description,
          systemPrompt,
          userPrompt,
          welcomeMessage,
          toolPluginUids: pluginUids,
          knowledgeBaseUids: kbaseUids,
          agenticMode,
          maxAgenticRounds,
          agenticTimeoutMinutes,
        },
        onFinished,
        onFinished
      )
    );
  };

  return (
    <div className="card textareas_panel">
      <div className="card_header">
        <div className="card_title"></div>
        <div className="card_actions">
          <button
            className="btn btn-outline-dark btn-sm"
            disabled={saveInProgress || !hasChanges}
            onClick={onSave}
          >
            {saveInProgress ? (
              <Spinner size="18" />
            ) : (
              <CheckIcon size="18" color="#67748e" strokeWidth="2" />
            )}
            <Message id="save" />
          </button>
        </div>
      </div>
      <div className="assistant_content_body">
        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_description" />
          </label>
          <textarea
            rows="3"
            onChange={onDescriptionChange}
            value={description}
            placeholder={label(intl, "assistant_description_placeholder")}
            className="form-control"
          ></textarea>
        </div>

        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_system_prompt" />
          </label>
          <textarea
            rows="6"
            onChange={onSystemPromptChange}
            value={systemPrompt}
            placeholder={label(intl, "assistant_system_prompt_placeholder")}
            className="form-control"
          ></textarea>
        </div>
        {/*
        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_user_prompt" />
          </label>
          <textarea
            rows="6"
            onChange={onUserPromptChange}
            value={userPrompt}
            placeholder={label(intl, "assistant_user_prompt_placeholder")}
            className="form-control"
          ></textarea>
        </div>
        */}
        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_welcome_message" />
          </label>
          <textarea
            rows="3"
            onChange={onWelcomeMessageChange}
            value={welcomeMessage}
            placeholder={label(intl, "assistant_welcome_message_placeholder")}
            className="form-control"
          ></textarea>
        </div>

        {/* Plugins Section */}
        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_plugins" />
          </label>
          <div className="dropdown_add_row">
            <select
              className="form-control"
              value={selectedPluginUid}
              onChange={(e) => setSelectedPluginUid(e.target.value)}
            >
              <option value="">
                <Message id="select_plugin" />
              </option>
              {availablePluginsFiltered.map((plugin) => (
                <option key={plugin.uid} value={plugin.uid}>
                  {plugin.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-dark btn-sm"
              onClick={onAddPlugin}
              disabled={!selectedPluginUid}
            >
              <PlusIcon color="#344767" strokeWidth="2" size="18" />
              <Message id="add" />
            </button>
          </div>
          <div className="tiles_container">
            {pluginUids.map((uid) => {
              const plugin = getPluginById(uid);
              return (
                <div key={uid} className="item_tile">
                  <UnplugIcon size="18" color="#67748e" className="tile_icon" />
                  <span className="tile_name">{plugin?.name || uid}</span>
                  <button
                    className="btn no-btn btn-sm tile_remove"
                    onClick={() => onRemovePlugin(uid)}
                  >
                    <TrashIcon size="16" color="#67748e" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Knowledge Section */}
        <div className="field_group">
          <label className="field_label">
            <Message id="assistant_knowledge" />
          </label>
          <div className="dropdown_add_row">
            <select
              className="form-control"
              value={selectedKBaseUid}
              onChange={(e) => setSelectedKBaseUid(e.target.value)}
            >
              <option value="">
                <Message id="select_knowledge_base" />
              </option>
              {availableKBasesFiltered.map((kbase) => (
                <option key={kbase.uid} value={kbase.uid}>
                  {kbase.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-outline-dark btn-sm"
              onClick={onAddKBase}
              disabled={!selectedKBaseUid}
            >
              <PlusIcon color="#344767" strokeWidth="2" size="18" />
              <Message id="add" />
            </button>
          </div>
          <div className="tiles_container">
            {kbaseUids.map((uid) => {
              const kbase = getKBaseById(uid);
              return (
                <div key={uid} className="item_tile">
                  <BookIcon size="18" color="#67748e" className="tile_icon" />
                  <span className="tile_name">{kbase?.name || uid}</span>
                  <button
                    className="btn no-btn btn-sm tile_remove"
                    onClick={() => onRemoveKBase(uid)}
                  >
                    <TrashIcon size="16" color="#67748e" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Agentic Mode Section */}
        <div className="field_group">
          <label className="field_label">
            <input
              type="checkbox"
              checked={agenticMode}
              onChange={(e) => setAgenticMode(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <Message id="assistant_agentic_mode" />
          </label>
          {agenticMode && (
            <div style={{ marginTop: '12px', paddingLeft: '24px' }}>
              <div className="field_row" style={{ marginBottom: '12px' }}>
                <label style={{ display: 'inline-block', width: '150px' }}>
                  <Message id="assistant_max_agentic_rounds" />:
                </label>
                <input
                  type="number"
                  value={maxAgenticRounds}
                  onChange={(e) => setMaxAgenticRounds(parseInt(e.target.value) || 10)}
                  min="1"
                  max="50"
                  className="form-control"
                  style={{ width: '100px', display: 'inline-block' }}
                />
              </div>
              <div className="field_row">
                <label style={{ display: 'inline-block', width: '150px' }}>
                  <Message id="assistant_agentic_timeout_minutes" />:
                </label>
                <input
                  type="number"
                  value={agenticTimeoutMinutes}
                  onChange={(e) => setAgenticTimeoutMinutes(parseInt(e.target.value) || 30)}
                  min="5"
                  max="120"
                  className="form-control"
                  style={{ width: '100px', display: 'inline-block' }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="field_group bottom_actions">
          <button
            className="btn bg-gradient-dark"
            disabled={saveInProgress || !hasChanges}
            onClick={onSave}
          >
            {saveInProgress ? (
              <Spinner size="18" />
            ) : (
              <CheckIcon size="18" color="#fff" strokeWidth="2" />
            )}
            <Message id="save" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantContent;
